import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Collapse, Tab, TabProps, Tabs } from '@mui/material';
import { JBColors } from '@bjb-gcrm/gcrm-frontend-components';
import { setShowSecondLevelTabs } from 'reducers/partnerFormReducer';
import { selectShowSecondLevelTabs } from 'selectors/partnerFormSelectors';
import { PartnerFormCategories, PartnerFormTabItems } from 'views/PartnerTabs/PrivateIndividualFormTabs';
import { BrFormCategories, BrTabItems } from 'views/BrTabs/BrTabs';

interface TabDescription {
  value: PartnerFormTabItems | BrTabItems;
  label: string | ReactNode;
  hidden?: boolean;
  category: PartnerFormCategories | BrFormCategories;
}

interface CollapsibleTabsProps {
  onTabChange: any;
  selectedTab: PartnerFormTabItems | BrTabItems;
  tabItems: (TabDescription & TabProps)[];
  secondLevelBackgroundColor: string;
}

const collapsibleTabButtonStyles = {
  padding: '0',
  width: 'fit-content',
  minWidth: 0,
  margin: 0,
  marginLeft: '30px',
  border: 'none',
  backgroundColor: 'transparent',
  transition: '.5s',
  '&:hover': {
    backgroundColor: 'transparent',
  },
};

/**
 * A component to render two a two-level navigation.
 * The top level tabs show the categories, which are automatically generated from props.tabItems.
 * The second level shows the tabs available within the selected category.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Function} props.onTabChange - Function to handle tab change events.
 * @param {PartnerFormTabItems | BrTabItems} props.selectedTab - Identifier for the currently active tab.
 * @param {(TabDescription & TabProps)[]} props.tabItems - Array of tab items.
 * @param {string} props.secondLevelBackgroundColor - Background color of the second tab level.
 * @returns {JSX.Element} CollapsibleTabs component.
 */
const TwoLevelTabNavigation = ({
  onTabChange,
  selectedTab,
  tabItems,
  secondLevelBackgroundColor,
}: CollapsibleTabsProps) => {
  const { t } = useTranslation();
  const nonHiddenTabs = useMemo(() => tabItems.filter((item) => !item.hidden), [tabItems]);
  const uniqueCategories = useMemo(() => {
    return [...new Set(nonHiddenTabs.map((item) => item.category))];
  }, [tabItems]);
  const singleTabCategories = useMemo(() => {
    const groupedTabs = new Map<PartnerFormCategories | BrFormCategories, TabDescription[]>();
    for (const tab of tabItems) {
      const tabsInCategory = groupedTabs.get(tab.category) ?? [];
      groupedTabs.set(tab.category, [...tabsInCategory, tab]);
    }
    const categories = new Set<PartnerFormCategories | BrFormCategories>();
    for (const [category, tabs] of groupedTabs) {
      if (tabs.length === 1) {
        categories.add(category);
      }
    }
    return categories;
  }, [tabItems]);
  const findTabsInCategory = (category: PartnerFormCategories | BrFormCategories) =>
    tabItems.filter((tabItem) => tabItem.category === category);
  const secondLevelTabItems = useMemo(
    () => tabItems.filter((tab) => !singleTabCategories.has(tab.category)),
    [tabItems, singleTabCategories],
  );
  const dispatch = useDispatch();
  const showSecondLevelTabs = useSelector(selectShowSecondLevelTabs);

  const findTabByValue = useCallback(
    (value: PartnerFormTabItems | BrTabItems) => {
      return tabItems.find((tabItem) => tabItem.value === value);
    },
    [tabItems],
  );

  useEffect(() => {
    return () => {
      //Reset on on unmount
      dispatch(setShowSecondLevelTabs(true));
    };
  }, []);

  useEffect(() => {
    //Reset to current category if user is in a different category but clicks inside content
    window.focus();

    function handleBlur() {
      const currentCategory = findTabByValue(selectedTab)?.category;
      currentCategory && setActiveCategory(currentCategory);
    }
    window.addEventListener('blur', handleBlur);

    // Cleanup function to remove the event listener
    return () => {
      window.removeEventListener('blur', handleBlur);
    };
  }, [selectedTab]);

  //The selected top level tab. On initial load, it selects the category of the tab received from the URL.
  const [activeCategory, setActiveCategory] = useState(findTabByValue(selectedTab)?.category ?? uniqueCategories[0]);

  useEffect(() => {
    const category = findTabByValue(selectedTab)?.category;
    if (category) {
      setActiveCategory(category);
      dispatch(setShowSecondLevelTabs(!singleTabCategories.has(category)));
    }
  }, [selectedTab]);

  return (
    <>
      <Tabs
        value={activeCategory}
        textColor="primary"
        variant="standard"
        sx={{
          backgroundColor: JBColors.white,
          height: '44px',
          '& .MuiTabs-indicator': {
            backgroundColor:
              JBColors.core.reflexBlue[findTabByValue(selectedTab)?.category === activeCategory ? 100 : 40],
          },
        }}
      >
        {uniqueCategories.map((category) => {
          const tabsInCategory = findTabsInCategory(category);
          if (singleTabCategories.has(category)) {
            const tab = tabsInCategory[0];
            return (
              <Tab
                onClickCapture={() => setActiveCategory(category)}
                value={category}
                onClick={(e) => {
                  onTabChange(e, tab.value);
                  dispatch(setShowSecondLevelTabs(false));
                }}
                key={category.toString()}
                id={`${tab.value}-tab`}
                data-testid={`${category.toString()}-tab`}
                label={tab.label}
                sx={{
                  ...collapsibleTabButtonStyles,
                  marginLeft: '32px',
                  borderBottom: `3px solid ${findTabByValue(selectedTab)?.category === category ? JBColors.core.reflexBlue[100] : 'transparent'}`,
                }}
              />
            );
          }
          return (
            <Tab
              onClickCapture={() => setActiveCategory(category)}
              value={category}
              onClick={(_) => {
                dispatch(setShowSecondLevelTabs(true));
              }}
              key={category.toString()}
              id={`${category.toString()}-category-tab`}
              data-testid={`${category.toString()}-category-tab`}
              label={t(`tabs.menu.categories.${category.toString()}`)}
              sx={{
                ...collapsibleTabButtonStyles,
                marginLeft: '32px',
                borderBottom: `3px solid ${findTabByValue(selectedTab)?.category === category ? JBColors.core.reflexBlue[100] : 'transparent'}`,
              }}
            />
          );
        })}
      </Tabs>
      <Collapse in={showSecondLevelTabs}>
        <Tabs
          value={selectedTab}
          onChange={onTabChange}
          textColor="primary"
          variant="standard"
          sx={{
            backgroundColor: secondLevelBackgroundColor,
            paddingLeft: '32px',
            height: '44px',
          }}
        >
          {secondLevelTabItems.map((tabItem) => (
            <Tab
              {...tabItem}
              id={`${tabItem.value}-tab`}
              data-testid={`${tabItem.value}-tab`}
              key={tabItem.value}
              sx={{
                fontSize: '14px',
                display: tabItem.hidden || tabItem.category !== activeCategory ? 'none' : 'inline-flex',
                ...tabItem.sx,
              }}
            />
          ))}
        </Tabs>
      </Collapse>
    </>
  );
};

export default TwoLevelTabNavigation;
